"use client"
import Link from "next/link"
import { useCallback, useState } from "react"
import PrimaryButton from "../buttons/PrimaryButton"
import FormField from "../form/FormField"
import { Heading2 } from "../text"

interface ContactFormProps {
  showOrganisation?: boolean
  showHeader?: boolean
  headerTextTitle?: string
}

const ContactForm: React.FC<ContactFormProps> = ({
  showOrganisation = true,
  showHeader = false,
  headerTextTitle = "Staat jouw vraag er niet tussen?",
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organisation: "",
    message: "",
  })

  const [status, setStatus] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      const requestBody = JSON.stringify({ formType: "contact", ...formData }) // Include formType in the request body

      try {
        const response = await fetch("/api/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: requestBody,
        })

        if (response.ok) {
          setStatus("Message sent successfully!")
          setFormData({ name: "", email: "", organisation: "", message: "" })
        } else {
          const data = await response.json()
          setStatus(data.message || "Failed to send message.")
        }
      } catch (error) {
        console.error("Error:", error)
        setStatus("Failed to send message.")
      }
    },
    [formData],
  )

  return (
    <>
      {showHeader && (
        <div className="pb-6">
          <div className="mx-auto max-w-lg text-center">
            <h4 className="font-bricolage font-extrabold text-[1.5rem] md:text-[2rem]">{headerTextTitle}</h4>
            <p className="font-bold text-[1rem] md:text-[1.125rem]">
              Neem dan contact op via&nbsp;
              <Link className="text-red" href="https://wa.me/+31629286807">
                Whatsapp
              </Link>
              ,&nbsp;of stuur een e-mail naar&nbsp;
              <Link className="text-red" href="mailto:hello@goodshift.nl">
                hello@goodshift.nl
              </Link>
              &nbsp;of vul het contactformulier in.
            </p>
          </div>
        </div>
      )}

      <div className="p-[2rem] bg-gray-20 rounded-xl">
        <div className="mx-auto max-w-2xl">
          <Heading2>Contact</Heading2>
        </div>
        <form onSubmit={handleSubmit} className="mx-auto mt-[1rem]">
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <FormField
              id="name"
              label="Naam *"
              type="text"
              value={formData.name}
              onChange={handleChange}
              autoComplete="name"
            />
            <FormField
              id="email"
              label="E-mailadres *"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />
            {showOrganisation && (
              <FormField
                id="organisation"
                label="Organisatie"
                type="text"
                value={formData.organisation}
                onChange={handleChange}
                autoComplete="organization"
              />
            )}
            <FormField
              id="message"
              label="Bericht *"
              type="textarea"
              value={formData.message}
              onChange={handleChange}
              rows={4}
            />
          </div>
          {status && <p className="mt-4 text-center">{status}</p>}
          <div className="mt-10">
            <PrimaryButton type="submit">Verstuur</PrimaryButton>
          </div>
        </form>
      </div>
    </>
  )
}

export default ContactForm
